import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
  Chip,
  LinearProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import Marb from "../../assests/images/marseg.svg";
import MarlboroRed from "../../assests/images/marlboro_red.svg";
import marlboroGold from "../../assests/images/marlboro_gold.svg";
import MarlboroGoldAdvance from "../../assests/images/marlboro-gold-advance.svg";
import marlboroFuseBeyond from "../../assests/images/marlboro-fuse-beyond.svg";
import MarlborocloveMix from "../../assests/images/marlboro-clove-mix.svg";
import MarlboroVistaForestFusion from "../../assests/images/marlboro-vista-forest-fusion.svg";
import { getDailySurveyOverview, getDailySurveyData } from "../../utils/apis";
import { useNavigate } from "react-router-dom";
import Icons from "../Icons";
import TopIcon from "../../assests/images/topIcon.svg";

const variantImagesRaw: Record<string, string> = {
  "marlboro red": MarlboroRed,
  "marlboro gold": marlboroGold,
  "marlboro gold advance": MarlboroGoldAdvance,
  "marlboro fuse beyond": marlboroFuseBeyond,
  "marlboro clove mix": MarlborocloveMix,
  "marlboro vista forest fusion": MarlboroVistaForestFusion,
};

const getVariantImage = (name: string) => variantImagesRaw[name.toLowerCase()] || Marb;

interface VariantSurvey {
  name: string;
  total_count: number;
  img_path?: string;
}

type Props = { contactId?: string | null };

const MonthlyOverview: React.FC<Props> = ({ contactId: contactIdProp }) => {
  const token = localStorage.getItem("token") || "";
  const derivedContactId = contactIdProp ?? (() => {
    try {
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.User?.ContactId || null;
    } catch { return null; }
  })();

  const now = new Date();
  const currentMonthShort = now.toLocaleString("en-US", { month: "short" });
  const currentYear = now.getFullYear();

  const [selectedMonth, setSelectedMonth] = useState(currentMonthShort);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [availableMonths, setAvailableMonths] = useState<string[]>([currentMonthShort]);
  const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);
  const [variants, setVariants] = useState<VariantSurvey[]>([]);
  const [progressTitle, setProgressTitle] = useState("");
  const [totalDisplay, setTotalDisplay] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
const [daysLeft, setDaysLeft] = useState("");
  useEffect(() => {
    if (!derivedContactId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [overviewRes, packcountRes] = await Promise.all([
          getDailySurveyOverview(token, derivedContactId, selectedMonth, String(selectedYear)),
          getDailySurveyData(token, selectedMonth, String(selectedYear), 1, derivedContactId),
        ]);

        const dataArr = overviewRes?.data ?? overviewRes;
        if (!dataArr || !Array.isArray(dataArr) || dataArr.length === 0) {
          setVariants([]);
          setTotalDisplay("");
          return;
        }

        const data = dataArr[0];
        setProgressTitle(data.progress || "");
        setVariants(data.variants || []);
setDaysLeft(data.days_left_in_target_month || "");
        if (data.month_filter?.length) {
          setAvailableMonths(
            data.month_filter.map((m: string) => m.charAt(0).toUpperCase() + m.slice(1))
          );
        }
        if (data.year_filter?.length) {
          setAvailableYears(data.year_filter);
        }

        const pcArr = packcountRes?.data ?? packcountRes;
        if (pcArr && Array.isArray(pcArr) && pcArr.length > 0) {
          setTotalDisplay(pcArr[0]["total_actual_vol/total_target_vol"] || "");
        }
      } catch (err) {
        console.error("Error fetching monthly survey overview:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [derivedContactId, selectedMonth, selectedYear]);

  const monthYearOptions = availableYears.flatMap((y) =>
    availableMonths.map((m) => ({ month: m, year: y }))
  );

  const [earnedStr, targetStr] = (totalDisplay || "").split("/");
  const totalEarned = Number(earnedStr) || 0;
  const totalTarget = Number(targetStr) || 0;
  const totalPercent = totalTarget ? (totalEarned / totalTarget) * 100 : 0;
  let pointsColor = "#E53935";
  if (totalPercent > 0 && totalPercent < 100) pointsColor = "#FB8C00";
  if (totalPercent >= 100) pointsColor = "#43A047";

  return (
    <>
      {/* GRADIENT HEADER */}
      <Box sx={{ background: "linear-gradient(360deg, rgb(249 249 149) 0%, rgb(255 255 243) 100%)", p: 2 }}>

        {/* TOP icon + title + chip */}
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flexWrap: "nowrap" }}>
          <Box display="flex" alignItems="center" gap={1.2} sx={{ flexShrink: 0 }}>
            <Box sx={{ background: "#ffced8", width: "40px", height: "40px", borderRadius: "30px", padding: "8px" }}>
              <img src={TopIcon} alt="scan Icon" style={{ width: "24px" }} />
            </Box>
            <Box>
              <Typography fontSize={13} color="#757575" sx={{ textAlign: "left" }}>TOP</Typography>
              <Typography fontWeight={600} fontSize={14} textAlign="left">Self Tracking</Typography>
            </Box>
          </Box>
          <Chip
            icon={<Icons name="timerImage" />}
            label={daysLeft}
            size="small"
            sx={{ bgcolor: "#f5f5f5", color: "#000", fontWeight: 500, borderRadius: "8px", fontSize: 12, height: 32, flexShrink: 0, ml: 1, mr: 1 }}
          />
        </Box>

        {/* POINTS DISPLAY */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 1, mb: 2, mr: 1 }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            background: "linear-gradient(180deg, #FFF4E5 0%, #FFF4E5 100%)",
            borderRadius: "12px", px: 2, py: 1, position: "relative", width: "fit-content", overflow: "hidden",
            "&::before": { content: '""', position: "absolute", top: 0, left: "10%", height: "2px", background: "linear-gradient(to right, #fff5ee 0%, #ff9800 50%, #fff5ee 100%)", width: "80%", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" },
            "&::after": { content: '""', position: "absolute", bottom: 0, left: "10%", height: "2px", background: "linear-gradient(to right, #fff5ee 0%, #ff9800 50%, #fff5ee 100%)", width: "80%", borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px" },
          }}>
            <Typography fontWeight={600} display="flex" alignItems="center" sx={{ zIndex: 1, fontSize: 14 }}>
              <Box component="span" sx={{ color: pointsColor, paddingRight: "3px" }}>{totalEarned}</Box>
              <Box component="span" sx={{ color: "#000" }}>/ {totalTarget}</Box>
            </Typography>
            <Box sx={{ zIndex: 1, lineHeight: 0, fontSize: "12px", fontWeight: "600" }}>Packs</Box>
          </Box>
        </Box>

        {/* PROGRESS BAR */}
        <Box mt={1.5} mb={0} mr={1}>
          <LinearProgress
            variant="determinate"
            value={totalPercent > 100 ? 100 : totalPercent}
            sx={{ height: 4, borderRadius: 5, backgroundColor: "#E0E0E0", "& .MuiLinearProgress-bar": { backgroundColor: "#4CAF50" } }}
          />
        </Box>
      </Box>

      {/* CONTENT SECTION */}
      <Box sx={{ background: "#FAFAFA", p: 2, borderRadius: 2, mt: 1 }}>

        {/* HEADER with month picker */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">{progressTitle || "Monthly Overview"}</Typography>
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, border: "1.5px solid #ddd", borderRadius: "10px", px: 1.5, py: 0.6, background: "#fff", cursor: "pointer" }}
          >
            <Typography fontWeight={700} fontSize={14}>{selectedMonth}</Typography>
            <Typography fontWeight={700} fontSize={14} sx={{ color: "#c5c5c5" }}>{selectedYear}</Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#333" }} />
          </Box>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { borderRadius: "10px", mt: 0.5 } }}>
            {monthYearOptions.map((opt, i) => (
              <MenuItem key={i} selected={opt.month === selectedMonth && opt.year === selectedYear} onClick={() => { setSelectedMonth(opt.month); setSelectedYear(opt.year); setAnchorEl(null); }} sx={{ fontSize: 14 }}>
                {opt.month} {opt.year}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* MONTHLY TOTAL */}
        {/* {totalDisplay && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", borderRadius: "10px", px: 2, py: 1.2, mb: 2, boxShadow: "0px 1px 4px rgba(0,0,0,0.07)" }}>
            <Typography fontSize={13} color="#666">Monthly Total</Typography>
            <Typography fontWeight={700} fontSize={15} color="#C8172F">{totalDisplay} Packs</Typography>
          </Box>
        )} */}

        {/* VARIANT LIST */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : variants.length === 0 ? (
          <Typography sx={{ textAlign: "center", color: "#999", py: 2, fontSize: 14 }}>
            No survey data for this month.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {variants.map((v, i) => {
              const image = v.img_path || getVariantImage(v.name);
              return (
                <Box key={i} sx={{ background: "#fff", borderRadius: 2, boxShadow: "0px 2px 4px rgba(0,0,0,0.05)", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: 48, height: 21, borderRadius: "0px", background: "transparent" }}>
                    <img src={image} style={{ width: "100%" }} alt={v.name} onError={(e) => { e.currentTarget.src = Marb; }} />
                  </Avatar>
                  <Typography fontWeight={600} fontSize={14} sx={{ flex: 1, textAlign: "left" }}>{v.name}</Typography>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                    <Typography fontWeight={800} fontSize={12} sx={{ color: "#46C65A" }}>{v.total_count}</Typography>
                    <Typography fontSize={13} sx={{ color: "#000" }}>Packs</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Button
          onClick={() => navigate(-1)}
          sx={{ mt: "20px", textTransform: "none", color: "#C8172F", fontWeight: 600, background: "#fff", border: "1px solid #C8172F", height: "44px", width: "120px", borderRadius: "10px", "&:hover": { color: "#fff" } }}
        >
          Back
        </Button>
      </Box>
    </>
  );
};

export default MonthlyOverview;
